import { useCallback } from 'react';
import { ConfirmDestructiveActionModal } from '@cotera/client/app/components/app';
import { useModalManager } from '@cotera/client/app/components/app/modal-manager/modal-manager';

/**
 * Confirms dropping a dataset column, resolving `true` to go ahead.
 *
 * Deleting a column persists a `delete_column` operation and takes its values
 * with it, so — unlike hiding — it warrants a guard, mirroring the workflow
 * delete confirm.
 */
export function useConfirmDeleteColumn(): (
  columnLabel: string
) => Promise<boolean> {
  const modalManager = useModalManager((s) => s.actions);

  return useCallback(
    (columnLabel: string) =>
      new Promise<boolean>((resolve) => {
        let settled = false;
        const settle = (confirmed: boolean): void => {
          if (settled) {
            return;
          }
          settled = true;
          resolve(confirmed);
        };

        modalManager.push({
          view: () => (
            <ConfirmDestructiveActionModal
              title={`Delete "${columnLabel}"?`}
              subtitle="This drops the column and every value in it from the dataset. It can't be recovered."
              confirmLabel="Delete column"
              onConfirm={() => {
                settle(true);
                modalManager.pop();
              }}
              onCancel={() => {
                settle(false);
                modalManager.pop();
              }}
            />
          ),
          // Escape or a backdrop click leaves the column alone.
          onClose: () => settle(false),
        });
      }),
    [modalManager]
  );
}
